import { useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, MapControls } from "@react-three/drei";
import { Vector3 } from "three";
import { store } from "../../store";
import {
    type OrbitControls as OrbitControlsThree,
    type MapControls as MapControlsThree,
} from "three-stdlib";
import { useListenGlobalBus } from "../hooks/use-global-bus";

export const OverviewCameraController = observer((): JSX.Element => {
    const controlsRef = useRef<OrbitControlsThree>(null);
    const { camera } = useThree();

    const { selectedAgvId, mode, agvs, viewMode } = store.serviceAgv;
    const effectiveSelectedAgvId = mode === "vehicles" ? selectedAgvId : null;

    const targetPos = useRef(new Vector3());
    const delta = useRef(new Vector3());

    const initialized = useRef(false);

    useListenGlobalBus("zoom_in", () => {
        controlsRef.current?.dollyIn();
    });
    useListenGlobalBus("zoom_out", () => {
        controlsRef.current?.dollyOut();
    });

    // Set camera position when view mode changes so it is as if it was position in the same spot
    useEffect(() => {
        if (!controlsRef.current || !effectiveSelectedAgvId) return;
        const agv = agvs.find((a) => a.id === effectiveSelectedAgvId);
        if (!agv) return;

        if (!initialized.current) {
            targetPos.current.set(agv.position.x, 0, agv.position.z);
            controlsRef.current.target.set(agv.position.x, 0, agv.position.z);
            camera.position.set(
                controlsRef.current.target.x,
                20,
                controlsRef.current.target.z + 15,
            );
            initialized.current = true;
            return;
        }
    }, [viewMode, effectiveSelectedAgvId, agvs, camera.position]);

    useFrame(() => {
        if (!controlsRef.current || !effectiveSelectedAgvId) return;
        const agv = agvs.find((a) => a.id === effectiveSelectedAgvId);
        if (!agv) return;

        targetPos.current.set(agv.position.x, 0, agv.position.z);

        // Compute how much the target moves this frame
        delta.current.copy(targetPos.current).sub(controlsRef.current.target);
        delta.current.multiplyScalar(0.05);

        // Move both target and camera by the same offset so the camera follows
        controlsRef.current.target.add(delta.current);
        camera.position.add(delta.current);
    });

    return (
        <OrbitControls
            ref={controlsRef}
            enableRotate={true}
            maxPolarAngle={Math.PI / 2.2}
            minDistance={5}
            maxDistance={60}
            enableDamping
            dampingFactor={0.1}
            enablePan={!effectiveSelectedAgvId}
        />
    );
});

export const MapCameraController = observer((): JSX.Element => {
    const controlsRef = useRef<MapControlsThree>(null);
    const { camera } = useThree();

    const { selectedAgvId, mode, agvs, viewMode } = store.serviceAgv;
    const effectiveSelectedAgvId = mode === "vehicles" ? selectedAgvId : null;

    const targetPos = useRef(new Vector3());
    const delta = useRef(new Vector3());

    const initialized = useRef(false);
    useListenGlobalBus("zoom_in", () => {
        controlsRef.current?.dollyIn();
    });
    useListenGlobalBus("zoom_out", () => {
        controlsRef.current?.dollyOut();
    });

    // Set camera position when view mode changes so it is as if it was position in the same spot
    useEffect(() => {
        if (!controlsRef.current || !effectiveSelectedAgvId) return;
        const agv = agvs.find((a) => a.id === effectiveSelectedAgvId);
        if (!agv) return;

        if (!initialized.current) {
            targetPos.current.set(agv.position.x, 0, agv.position.z);
            controlsRef.current.target.set(agv.position.x, 0, agv.position.z);
            camera.position.set(controlsRef.current.target.x, 40, controlsRef.current.target.z);
            initialized.current = true;
            return;
        }
    }, [viewMode, effectiveSelectedAgvId, agvs, camera.position]);

    useFrame(() => {
        if (!controlsRef.current || !effectiveSelectedAgvId) return;
        const agv = agvs.find((a) => a.id === effectiveSelectedAgvId);
        if (!agv) return;

        targetPos.current.set(agv.position.x, 0, agv.position.z);

        // Compute how much the target moves this frame
        delta.current.copy(targetPos.current).sub(controlsRef.current.target);
        delta.current.multiplyScalar(0.05);

        // Move both target and camera by the same offset so the camera follows
        controlsRef.current.target.add(delta.current);
        camera.position.add(delta.current);
    });

    return (
        <MapControls
            ref={controlsRef}
            maxZoom={50}
            minZoom={10}
            enableRotate={false}
            enableDamping
            dampingFactor={0.1}
            enablePan={!selectedAgvId}
        />
    );
});
